import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import SaveBar from "./SaveBar";
import { Button } from   "@/components/ui/button";
import { Badge } from   "@/components/ui/badge";
import { Shield, Users, Plus, Edit, Trash2, Eye, Lock, Unlock, Settings, Loader2 } from "lucide-react";
import apiService from "@/lib/api-functions/api-service";
import { getCompanyRoles, hasPermission } from "@/lib/utils/permissions";
import { getCompanyMembersWithRoles } from "@/lib/stores/unifiedUserStore";
import type { CompanyRole } from "@/lib/stores/storeTypes";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
type PermissionsSectionProps = Record<string, never>;

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  color: string;
  isBuiltIn: boolean;
  permissions: string[];
  userCount: number;
}

interface PermissionsSettings {
  roles: Role[];
  permissions: Permission[];
}

export default function PermissionsSection(_props: PermissionsSectionProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany } = useSimplifiedUserData();
  
  // Permissions state
  const [settings, setSettings] = useState<PermissionsSettings>({
    roles: [],
    permissions: []
  });

  const [originalSettings, setOriginalSettings] = useState<PermissionsSettings>(settings);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [newRole, setNewRole] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
    permissions: [] as string[]
  });
  const [loading, setLoading] = useState(false);
  
  // Check if user has permission to manage roles
  const canManageRoles = selectedCompany ? hasPermission(selectedCompany.id, 'roles.manage') : false;

  // Define available permissions by category
  const availablePermissions: Permission[] = [
    // Project Management
    { id: "projects.view", name: "View Projects", description: "Can view project list and details", category: "Project Management" },
    { id: "projects.create", name: "Create Projects", description: "Can create new projects", category: "Project Management" },
    { id: "projects.edit", name: "Edit Projects", description: "Can modify project settings", category: "Project Management" },
    { id: "projects.delete", name: "Delete Projects", description: "Can delete projects", category: "Project Management" },
    { id: "projects.manage_members", name: "Manage Project Members", description: "Can add/remove project members", category: "Project Management" },
    
    // Content Management
    { id: "content.view", name: "View Content", description: "Can view strings, documents, and translations", category: "Content Management" },
    { id: "content.create", name: "Create Content", description: "Can create new strings and documents", category: "Content Management" },
    { id: "content.edit", name: "Edit Content", description: "Can modify existing content", category: "Content Management" },
    { id: "content.delete", name: "Delete Content", description: "Can delete content", category: "Content Management" },
    { id: "content.approve", name: "Approve Translations", description: "Can approve/reject translations", category: "Content Management" },
    
    // Translation Management
    { id: "translations.view", name: "View Translations", description: "Can view translation status and progress", category: "Translation Management" },
    { id: "translations.translate", name: "Create Translations", description: "Can create and submit translations", category: "Translation Management" },
    { id: "translations.review", name: "Review Translations", description: "Can review and edit translations", category: "Translation Management" },
    { id: "translations.approve", name: "Approve Translations", description: "Can approve final translations", category: "Translation Management" },
    
    // Library Management
    { id: "library.view", name: "View Library", description: "Can view glossary and templates", category: "Library Management" },
    { id: "library.create", name: "Create Library Items", description: "Can create glossary entries and templates", category: "Library Management" },
    { id: "library.edit", name: "Edit Library Items", description: "Can modify glossary and templates", category: "Library Management" },
    { id: "library.delete", name: "Delete Library Items", description: "Can delete library content", category: "Library Management" },
    
    // User Management
    { id: "users.view", name: "View Users", description: "Can view company members and their roles", category: "User Management" },
    { id: "users.invite", name: "Invite Users", description: "Can invite new users to the company", category: "User Management" },
    { id: "users.manage_roles", name: "Manage User Roles", description: "Can assign/remove user roles", category: "User Management" },
    { id: "users.remove", name: "Remove Users", description: "Can remove users from company", category: "User Management" },
    
    // Analytics & Reporting
    { id: "analytics.view", name: "View Analytics", description: "Can view project analytics and reports", category: "Analytics & Reporting" },
    { id: "analytics.export", name: "Export Data", description: "Can export translation data and reports", category: "Analytics & Reporting" },
    
    // Settings & Administration
    { id: "settings.view", name: "View Settings", description: "Can access company settings", category: "Settings & Administration" },
    { id: "settings.edit", name: "Edit Settings", description: "Can modify company settings", category: "Settings & Administration" },
    { id: "settings.manage_roles", name: "Manage Roles", description: "Can create and modify user roles", category: "Settings & Administration" },
    { id: "settings.billing", name: "Manage Billing", description: "Can access billing and subscription settings", category: "Settings & Administration" },
    
    // Page Access Control
    { id: "pages.home.view", name: "Home Page", description: "Can access the home dashboard", category: "Page Access" },
    { id: "pages.library.view", name: "Library Page", description: "Can access the library/glossary page", category: "Page Access" },
    { id: "pages.templates.view", name: "Templates Page", description: "Can access the templates/blueprints page", category: "Page Access" },
    { id: "pages.documents.view", name: "Documents Page", description: "Can access the documents page", category: "Page Access" },
    { id: "pages.dialogs.view", name: "Dialogs Page", description: "Can access the dialogs page", category: "Page Access" },
    { id: "pages.tms.view", name: "TMS Page", description: "Can access the translation management system", category: "Page Access" },
    { id: "pages.textManagement.view", name: "Text Management Page", description: "Can access the text management page", category: "Page Access" },
    { id: "pages.types.view", name: "Types Page", description: "Can access the types page", category: "Page Access" },
    { id: "pages.globals.view", name: "Globals Page", description: "Can access the globals page", category: "Page Access" },
    { id: "pages.ui.view", name: "UI Page", description: "Can access the UI configuration page", category: "Page Access" },
    { id: "pages.images.view", name: "Images Page", description: "Can access the images page", category: "Page Access" },
    { id: "pages.context.view", name: "Context Page", description: "Can access the context page", category: "Page Access" },
    { id: "pages.relationships.view", name: "Relationships Page", description: "Can access the relationships page", category: "Page Access" },
    { id: "pages.logicFlows.view", name: "Logic Flows Page", description: "Can access the logic flows page", category: "Page Access" },
    { id: "pages.settings.view", name: "Settings Page", description: "Can access company settings", category: "Page Access" },
    { id: "pages.profile.view", name: "Profile Page", description: "Can access user profile settings", category: "Page Access" },
  ];


  // Initialize settings
  useEffect(() => {
    if (selectedCompany?.id) {
      loadPermissionsSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.id]);

  const loadPermissionsSettings = async () => {
    if (!selectedCompany?.id) return;
    
    setLoading(true);
    try {
      // Fetch real roles from the database
      const rolesResponse = await apiService.getCompanyRoles(selectedCompany.id);

      if (!rolesResponse.success || !rolesResponse.data) {
        throw new Error('Failed to fetch roles');
      }

      // Get company members to calculate user counts
      const companyMembers = getCompanyMembersWithRoles(selectedCompany.id);
      
      // Count users per role
      const userCountsByRole: Record<string, number> = {};
      companyMembers.forEach(member => {
        const roleName = member.role.toLowerCase();
        userCountsByRole[roleName] = (userCountsByRole[roleName] || 0) + 1;
      });

      // Map database roles to UI role format
      const mappedRoles: Role[] = rolesResponse.data.map((dbRole) => {
        const roleName = dbRole.role_name.toLowerCase();
        
        // Use color from data field if available, otherwise use default based on role name
        let color = dbRole.data?.color;
        
        if (!color) {
          // Fallback to default colors based on role name
          color = "#3B82F6"; // Default blue
          if (roleName === "owner") color = "#DC2626";
          else if (roleName === "admin" || roleName === "administrator") color = "#7C3AED";
          else if (roleName.includes("manager")) color = "#059669";
          else if (roleName.includes("translator")) color = "#2563EB";
          else if (roleName.includes("reviewer")) color = "#EA580C";
        }

        // Convert permission object to array of permission keys
        const permissionKeys = Object.entries(dbRole.permissions)
          .filter(([_, value]) => value === true)
          .map(([key, _]) => key);

        console.log(`[PermissionsSection] Role "${dbRole.role_name}" permissions:`, permissionKeys);

        return {
          id: dbRole.id,
          name: dbRole.role_name,
          description: dbRole.description || '',
          color: color,
          isBuiltIn: dbRole.is_built_in,
          permissions: permissionKeys,
          userCount: userCountsByRole[roleName] || 0
        };
      });

      const loadedSettings: PermissionsSettings = {
        roles: mappedRoles,
        permissions: availablePermissions
      };

      setSettings(loadedSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(loadedSettings)));
    } catch (error) {
      console.error("Failed to load permissions settings:", error);
      toast({
        title: "Error",
        description: "Failed to load permissions settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if there are any changes
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // Group permissions by category
  const permissionsByCategory = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Handle role creation
  const handleCreateRole = async () => {
    if (!selectedCompany?.id) return;
    
    if (!newRole.name.trim()) {
      toast({
        title: "Role name required",
        description: "Please enter a name for the role",
        variant: "destructive",
      });
      return;
    }

    if (settings.roles.some(role => role.name.toLowerCase() === newRole.name.toLowerCase())) {
      toast({
        title: "Role already exists",
        description: "A role with this name already exists",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Convert permissions array to object format
      const permissionsObject: Record<string, boolean> = {};
      newRole.permissions.forEach(permKey => {
        permissionsObject[permKey] = true;
      });

      // Create role via API with color in data field
      const response = await apiService.createCompanyRole({
        company_id: selectedCompany.id,
        role_name: newRole.name.trim(),
        description: newRole.description || undefined,
        data: {
          color: newRole.color
        },
        permissions: permissionsObject
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create role');
      }

      // Reload roles to get updated data
      await loadPermissionsSettings();

      setNewRole({ name: "", description: "", color: "#3B82F6", permissions: [] });
      setShowCreateRole(false);

      toast({
        title: "Role created",
        description: `Role "${newRole.name}" has been created successfully`,
      });
    } catch (error: any) {
      console.error("Failed to create role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create role",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle role editing
  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name,
      description: role.description,
      color: role.color,
      permissions: [...role.permissions]
    });
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    setLoading(true);
    try {
      // Convert permissions array to object format
      const permissionsObject: Record<string, boolean> = {};
      newRole.permissions.forEach(permKey => {
        permissionsObject[permKey] = true;
      });

      // Update role via API with color in data field
      const response = await apiService.updateRolePermissions({
        role_id: editingRole.id,
        description: newRole.description || undefined,
        data: {
          color: newRole.color
        },
        permissions: permissionsObject
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update role');
      }

      // Reload roles to get updated data
      await loadPermissionsSettings();

      setEditingRole(null);
      setNewRole({ name: "", description: "", color: "#3B82F6", permissions: [] });

      toast({
        title: "Role updated",
        description: `Role "${newRole.name}" has been updated successfully`,
      });
    } catch (error: any) {
      console.error("Failed to update role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle role deletion
  const handleDeleteRole = async (roleId: string) => {
    const role = settings.roles.find(r => r.id === roleId);
    if (!role) return;

    if (role.isBuiltIn) {
      toast({
        title: "Cannot delete built-in role",
        description: "Built-in roles cannot be deleted",
        variant: "destructive",
      });
      return;
    }

    if (role.userCount > 0) {
      toast({
        title: "Cannot delete role",
        description: "This role is assigned to users. Please reassign users before deleting.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Delete role via API
      const response = await apiService.deleteCompanyRole(roleId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete role');
      }

      // Reload roles to get updated data
      await loadPermissionsSettings();

      toast({
        title: "Role deleted",
        description: `Role "${role.name}" has been deleted`,
      });
    } catch (error: any) {
      console.error("Failed to delete role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete role",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle permission toggle
  const handlePermissionToggle = (permissionId: string, checked: boolean) => {
    if (editingRole || showCreateRole) {
      setNewRole(prev => ({
        ...prev,
        permissions: checked
          ? [...prev.permissions, permissionId]
          : prev.permissions.filter(p => p !== permissionId)
      }));
    }
  };

  // Handle save
  const handleSave = async () => {
    // Reload to sync with server
    await loadPermissionsSettings();
    
    toast({
      title: "Success",
      description: "Permissions settings refreshed successfully",
    });
  };

  // Handle cancel
  const handleCancel = () => {
    loadPermissionsSettings();
    setSelectedRole(null);
    setShowCreateRole(false);
    setEditingRole(null);
    setNewRole({ name: "", description: "", color: "#3B82F6", permissions: [] });
  };

  const selectedRoleData = selectedRole ? settings.roles.find(r => r.id === selectedRole) : null;

  return (
    <div>
      {!canManageRoles && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            You need role management permissions to create or modify roles. Contact your company owner.
          </p>
        </div>
      )}

      {loading && (
        <div className="mb-4 flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-muted-foreground">{t('platform.settings.permissionssection.loading_roles')}</span>
        </div>
      )}

      <div className="max-w-6xl space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8"> 
          {/* Roles List */}
          <div className="lg:col-span-1">
            <div className="bg-background p-6 rounded border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.permissionssection.roles')}</h3>
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowCreateRole(true)}
                  className="flex items-center space-x-1"
                  disabled={!canManageRoles || loading}
                >
                  <Plus className="h-4 w-4" />
                  <span>{t('platform.settings.permissionssection.new_role')}</span>
                </Button>
              </div>

              <div className="space-y-2">
                {settings.roles.map((role) => (
                  <div
                    key={role.id}
                    className={`p-3 rounded border cursor-pointer transition-colors ${
                      selectedRole === role.id ? 'border-blue-500 bg-blue-50' : 'border-input hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedRole(role.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: role.color }}
                        />
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-muted-foreground">{role.name}</span>
                            {role.isBuiltIn && (
                              <Badge variant="secondary" className="text-xs">Built-in</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{role.userCount} users</p>
                        </div>
                      </div>
                      {!role.isBuiltIn && (
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditRole(role);
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRole(role.id);
                            }}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Role Details & Permissions */}
          <div className="lg:col-span-2">
            {(selectedRoleData || showCreateRole || editingRole) ? (
              <div className="bg-background p-6 rounded border">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-medium text-muted-foreground">
                      {showCreateRole ? "Create New Role" : editingRole ? "Edit Role" : "Role Permissions"}
                    </h3>
                  </div>
                  {(showCreateRole || editingRole) && (
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowCreateRole(false);
                          setEditingRole(null);
                          setNewRole({ name: "", description: "", color: "#3B82F6", permissions: [] });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={editingRole ? handleUpdateRole : handleCreateRole}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            {editingRole ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          editingRole ? "Update Role" : "Create Role"
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Role Basic Information */}
                {(showCreateRole || editingRole) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
                    <div>
                      <label htmlFor="role-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Role Name *
                      </label>
                      <input
                        type="text"
                        id="role-name"
                        value={newRole.name}
                        onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('platform.settings.permissionssection.enter_role_name')}
                        className="w-full px-3 py-2 border border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="role-color" className="block text-sm font-medium text-gray-700 mb-2">
                        Color
                      </label>
                      <input
                        type="color"
                        id="role-color"
                        value={newRole.color}
                        onChange={(e) => setNewRole(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full h-10 border border-input rounded"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="role-description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="role-description"
                        value={newRole.description}
                        onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={t('platform.settings.permissionssection.describe_what_this_role_can_do')}
                        rows={2}
                        className="w-full px-3 py-2 border border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                {/* Selected Role Info */}
                {selectedRoleData && !editingRole && !showCreateRole && (
                  <div className="mb-6 p-4 bg-gray-50 rounded">
                    <div className="flex items-center space-x-3 mb-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: selectedRoleData.color }}
                      />
                      <h4 className="text-lg font-medium text-muted-foreground">{selectedRoleData.name}</h4>
                      {selectedRoleData.isBuiltIn && (
                        <Badge variant="secondary">{t('platform.settings.permissionssection.built_in_2')}</Badge>
                      )}
                    </div>
                      <p className="text-muted-foreground mb-2">{selectedRoleData.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedRoleData.userCount} users • {selectedRoleData.permissions.length} permissions
                    </p>
                    {selectedRoleData.permissions.length === 0 && (
                      <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-xs text-blue-700">
                          <Lock className="inline h-3 w-3 mr-1" />
                          This role has no permissions set and therefore has <strong>full access</strong> to everything.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Permissions Matrix */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-4">{t('platform.settings.permissionssection.permissions')}</h4>
                  <div className="space-y-6">
                    {Object.entries(permissionsByCategory).map(([category, permissions]) => (
                      <div key={category} className="border border-gray-200 rounded p-4">
                        <h5 className="font-medium text-muted-foreground mb-3">{category}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {permissions.map((permission) => {
                            let isChecked = false;
                            
                            if (showCreateRole || editingRole) {
                              // In create/edit mode, check against newRole.permissions
                              isChecked = newRole.permissions.includes(permission.id);
                            } else if (selectedRoleData) {
                              // In view mode, check if role has this specific permission or wildcard
                              // If no permissions are set at all, treat as wildcard (full access)
                              const hasNoPermissions = selectedRoleData.permissions.length === 0;
                              isChecked = hasNoPermissions || 
                                         selectedRoleData.permissions.includes(permission.id) || 
                                         selectedRoleData.permissions.includes('*');
                            }
                            
                            return (
                              <div key={permission.id} className="flex items-start space-x-3">
                                <input
                                  type="checkbox"
                                  id={permission.id}
                                  checked={isChecked}
                                  onChange={(e) => handlePermissionToggle(permission.id, e.target.checked)}
                                  disabled={!showCreateRole && !editingRole}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded mt-0.5"
                                />
                                <div className="flex-1">
                                  <label
                                    htmlFor={permission.id}
                                    className="text-sm font-medium text-muted-foreground cursor-pointer"
                                  >
                                    {permission.name}
                                  </label>
                                  <p className="text-xs text-muted-foreground">{permission.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-background p-6 rounded border">
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground mb-2">{t('platform.settings.permissionssection.select_a_role')}</h3>
                  <p className="text-muted-foreground">
                    Choose a role from the list to view and manage its permissions
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-background p-4 rounded border border-input">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('platform.settings.permissionssection.permission_summary')}</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <span className="font-medium">{t('platform.settings.permissionssection.total_roles')}</span> {settings.roles.length} ({settings.roles.filter(r => !r.isBuiltIn).length} custom)
            </p>
            <p>
              <span className="font-medium">{t('platform.settings.permissionssection.total_users')}</span> {settings.roles.reduce((sum, role) => sum + role.userCount, 0)}
            </p>
            <p>
              <span className="font-medium">{t('platform.settings.permissionssection.permission_categories')}</span> {Object.keys(permissionsByCategory).length}
            </p>
          </div>
        </div>
      </div>

      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />
    </div>
  );
}
